import { useState } from "react";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import moment from "moment";

import { Select } from "components/ui/Select/Select";
import { useAddConsultationService } from "features/consultations/hooks/useAddConsultationService";
import { usePracticeServices } from "features/practice/hooks/usePracticeServices";

import { PaymentSection } from "../ConsultationBox/components/PaymentSection.jsx";
import style from "./ConsultationBoxReadOnly.module.css";

const formatConsultationType = (type) => {
  if (!type) return "";

  return type
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatKey = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const parseAdditionalData = (additionalData) => {
  if (!additionalData) return null;

  try {
    const data =
      typeof additionalData === "string"
        ? JSON.parse(additionalData)
        : additionalData;

    if (!data || typeof data !== "object" || Object.keys(data).length === 0) {
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error parsing additional data:", error);

    return null;
  }
};

export const ConsultationBoxReadOnly = ({ consultation, isReadOnly }) => {
  const [service, setService] = useState("Select Service");

  const hasActiveConsultation = !!consultation;
  const isPaid = consultation?.moneyOwed === false;

  const appointment = consultation?.appointment;
  const hasAppointment = !!appointment;
  const referrerName = appointment?.referrer?.name;
  const referrerEmail = appointment?.referrer?.email || appointment?.referrerEmail;
  const additionalData = parseAdditionalData(appointment?.additionalData);
  const showAppointmentTab = hasAppointment;
  const showDetailsTab = !!additionalData;

  const [activeTab, setActiveTab] = useState(
    showAppointmentTab ? "appointment" : "services"
  );

  const { data: services, isLoading: isServicesLoading } =
    usePracticeServices();

  const addConsultationService = useAddConsultationService(consultation?.id);

  return (
    <>
      <div className={style.consultationBoxWrapper}>
        <section className={style.consultationBox}>
          <div className={style.consultationBoxTop}>
            <div className={style.consultationBoxTopItem}>
              <h2 className={style.consultationTitle}>
                {consultation?.title || "Consultation"}
              </h2>
            </div>
            <div className={style.rightBox}>
              <p>
                <span>Doctor:</span>{" "}
                {consultation?.consultationStaff?.firstName}{" "}
                {consultation?.consultationStaff?.lastName}
              </p>
              <p>
                <span>Consultation date:</span>{" "}
                {moment(consultation?.created, "DD-MM-YYYY HH:mm").format(
                  "DD-MM-YYYY HH:mm"
                )}
              </p>
            </div>
          </div>

          {(showAppointmentTab || showDetailsTab) && (
            <div className={style.tabsContainer}>
              <div className={style.tabsWrapper}>
                {showAppointmentTab && (
                  <button
                    type="button"
                    className={`${style.tab} ${
                      activeTab === "appointment" ? style.tabActive : ""
                    }`}
                    onClick={() => setActiveTab("appointment")}
                  >
                    Appointment
                  </button>
                )}
                {showDetailsTab && (
                  <button
                    type="button"
                    className={`${style.tab} ${
                      activeTab === "details" ? style.tabActive : ""
                    }`}
                    onClick={() => setActiveTab("details")}
                  >
                    Details
                  </button>
                )}
                <button
                  type="button"
                  className={`${style.tab} ${
                    activeTab === "services" ? style.tabActive : ""
                  }`}
                  onClick={() => setActiveTab("services")}
                >
                  Payment
                </button>
              </div>
            </div>
          )}

          <div className={style.tabContent}>
            {activeTab === "appointment" && showAppointmentTab && (
              <div className={style.appointmentDetails}>
                <div className={style.appointmentHeader}>
                  <div className={style.appointmentHeaderMain}>
                    <span className={style.appointmentHeaderLabel}>
                      Appointment
                    </span>
                    <h3 className={style.appointmentHeaderDate}>
                      {appointment.startTime
                        ? dayjs(appointment.startTime).format("ddd, DD MMM YYYY")
                        : "—"}
                    </h3>
                    <p className={style.appointmentHeaderTime}>
                      {appointment.startTime
                        ? dayjs(appointment.startTime).format("HH:mm")
                        : "—"}
                      {!!appointment.durationInMins && (
                        <span className={style.appointmentHeaderDuration}>
                          · {appointment.durationInMins} mins
                        </span>
                      )}
                    </p>
                  </div>
                  {!!appointment.consultationType && (
                    <span
                      className={`${style.appointmentTypeBadge} ${
                        appointment.consultationType === "VIDEO"
                          ? style.appointmentTypeBadgeVideo
                          : appointment.consultationType === "PHONE"
                          ? style.appointmentTypeBadgePhone
                          : style.appointmentTypeBadgeInPerson
                      }`}
                    >
                      {formatConsultationType(appointment.consultationType)}
                    </span>
                  )}
                </div>

                <div className={style.appointmentInfoGrid}>
                  {!!appointment.assignedToStaffMember && (
                    <div className={style.appointmentInfoItem}>
                      <span className={style.appointmentInfoLabel}>
                        Staff Member
                      </span>
                      <span className={style.appointmentInfoValue}>
                        {`${
                          appointment.assignedToStaffMember.firstName ?? ""
                        } ${
                          appointment.assignedToStaffMember.lastName ?? ""
                        }`.trim()}
                      </span>
                    </div>
                  )}
                  {!!(appointment.practiceService?.name || appointment.serviceName) && (
                    <div className={style.appointmentInfoItem}>
                      <span className={style.appointmentInfoLabel}>Service</span>
                      <span className={style.appointmentInfoValue}>
                        {appointment.practiceService?.name || appointment.serviceName}
                      </span>
                    </div>
                  )}
                  {!!appointment.companyName && (
                    <div className={style.appointmentInfoItem}>
                      <span className={style.appointmentInfoLabel}>Company</span>
                      <span className={style.appointmentInfoValue}>
                        {appointment.companyName}
                      </span>
                    </div>
                  )}
                  {!!appointment.email && (
                    <div className={style.appointmentInfoItem}>
                      <span className={style.appointmentInfoLabel}>Email</span>
                      <a
                        href={`mailto:${appointment.email}`}
                        className={`${style.appointmentInfoValue} ${style.appointmentInfoLink}`}
                      >
                        {appointment.email}
                      </a>
                    </div>
                  )}
                  <div className={style.appointmentInfoItem}>
                    <span className={style.appointmentInfoLabel}>
                      Telephone
                    </span>
                    {appointment.telephoneNumber ? (
                      <a
                        href={`tel:${appointment.telephoneNumber}`}
                        className={`${style.appointmentInfoValue} ${style.appointmentInfoLink}`}
                      >
                        {appointment.telephoneNumber}
                      </a>
                    ) : (
                      <span className={style.appointmentInfoValue}>—</span>
                    )}
                  </div>
                  {!!(referrerName || referrerEmail) && (
                    <div className={style.appointmentInfoItem}>
                      <span className={style.appointmentInfoLabel}>
                        Referrer
                      </span>
                      {!!referrerName && (
                        <span className={style.appointmentInfoValue}>
                          {referrerName}
                        </span>
                      )}
                      {!!referrerEmail && (
                        <a
                          href={`mailto:${referrerEmail}`}
                          className={`${style.appointmentInfoValue} ${style.appointmentInfoLink}`}
                        >
                          {referrerEmail}
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {!!(appointment.reasonForVisit || appointment.reasonDescription) && (
                  <div className={style.appointmentReasonSection}>
                    {!!appointment.reasonForVisit && (
                      <div className={style.appointmentReasonItem}>
                        <span className={style.appointmentInfoLabel}>
                          Reason for Visit
                        </span>
                        <span className={style.appointmentReasonValue}>
                          {appointment.reasonForVisit}
                        </span>
                      </div>
                    )}
                    {!!appointment.reasonDescription && (
                      <div className={style.appointmentReasonItem}>
                        <span className={style.appointmentInfoLabel}>
                          Reason Description
                        </span>
                        <p className={style.appointmentReasonDescription}>
                          {appointment.reasonDescription}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "details" && showDetailsTab && (
              <div className={style.appointmentDetails}>
                <div className={style.additionalDataGrid}>
                  {Object.entries(additionalData).map(([key, value]) => (
                    <div key={key} className={style.appointmentInfoItem}>
                      <span className={style.appointmentInfoLabel}>
                        {formatKey(key)}
                      </span>
                      <span className={style.appointmentInfoValue}>
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(activeTab === "services" || (!showAppointmentTab && !showDetailsTab)) && (
              <>
                {hasActiveConsultation && !isReadOnly && (
                  <div className={style.addServiceWrapper}>
                    <Select
                      name="service"
                      options={
                        isServicesLoading
                          ? []
                          : (services ?? []).map((item, index) => ({
                              label: item.name,
                              value: String(item.id || index),
                            }))
                      }
                      value={service}
                      onChange={(value) => setService(value)}
                      placeholder="Select Service"
                      disabled={isPaid}
                      fullWidth
                    />
                    <button
                      className={style.addServiceButton}
                      onClick={() => {
                        if (service === "Select Service") {
                          toast.error("Please select a service");

                          return;
                        }
                        addConsultationService.mutate(service);
                      }}
                      disabled={isPaid}
                    >
                      Add additional service
                    </button>
                  </div>
                )}
                <PaymentSection
                  consultation={consultation}
                  extraTopMargin={isReadOnly && !showAppointmentTab && !showDetailsTab}
                />
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
};
